import { Component, ojs, app, State, v, state } from "modular-openscriptjs";
import { pageInfo, either } from "../../helpers";
import { api, PAGES } from "../../declarations";
import { $e } from "../../ojs.events";

const h = app("h");
const broker = app("broker");
const gc = app("gc");

export default class AuthenticatedIndex extends Component {
	ratings = state(0);
	stats = state({
		processing_now: 0,
		ready_now: 0,
		delivering_now: 0,
		delivered_this_month: 0,
		delivered_last_month: 0,
		delivered_on_time: 0,
		delivered_late: 0,
	});

	async getDashboardInfo() {
		try {
			const companyId = window.currentCompany?.id;
			if (!companyId) return;

			const res = await api.get(
				`/companies/${companyId}/deliverer/stats`,
				{},
				{ silent: true }
			);
			if (res && res.data) {
				this.ratings.value = res.data.rating ?? 0;
				this.stats.value = res.data;
			}
		} catch (e) {
			console.error(e);
		}
	}

	render(...args) {
		this.getDashboardInfo();
		pageInfo(PAGES.HOME);

		return h.div(
			{ class: "pt-3 pb-5 mb-5" },

			h.div(
				{ class: "container" },
				h.div(
					{ class: "row g-4" },

					// Left Column: Quick Actions + Stats (Order 2 on mobile, 1 on desktop)
					h.div(
						{ class: "col-12 col-lg-8 order-2 order-lg-1" },
						h.div(
							{ class: "row g-3" },

							// Quick Actions Block
							h.div(
								{ class: "col-12" },
								h.h5({ class: "fw-bold text-dark mb-3" }, "Quick Actions"),
								h.div(
									{ class: "row g-3" },
									this.renderActionCard(
										"Process Order",
										"Manage active orders",
										"clipboard-check",
										"primary",
										h.func("app('router').to", "company.orders"),
									),
									h.div(
										{ class: "col-6 col-md-3" },
										v(gc.user, (user) => {
											const isOnline = user?.value?.deliverer_status === "online";
											return this.renderActionCardInner(
												isOnline ? "Go Offline" : "Go Online",
												isOnline ? "Stop receiving orders" : "Start receiving orders",
												"power",
												isOnline ? "danger" : "success",
												this.method("changeOnlineStatus", [user?.value?.deliverer_status]),
											);
										})
									),
									this.renderActionCard(
										"Reviews",
										"See customer feedback",
										"star",
										"info",
										h.func("app('router').to", "rider.reviews"),
									),
									this.renderActionCard(
										"Awards",
										"View achievements",
										"award",
										"morning",
										h.func("app('router').to", "user.awards"),
									),
								)
							),

							// Statistics Cards Block
							h.div(
								{ class: "col-12 mt-4" },
								h.h5(
									{ class: "fw-bold text-dark mb-3" },
									"Rider Performance Stats"
								),

								v(this.stats, (s) =>
									h.div(
										{ class: "row g-3" },
										// Row 1: 2 Cards (Processing/Ready vs Delivering)
										h.div(
											{ class: "row g-3 mb-1 mx-0 px-0" },
											h.div(
												{ class: "col-6" },
												h.div(
													{ class: "card border shadow-sm rounded-4 h-100" },
													h.div(
														{ class: "card-body p-3" },
														v(gc.user, (user) => {
															const isCoordinator = user?.value?.type === "purchase_coordinator";
															const icon = isCoordinator ? "arrow-repeat" : "bag-check";
															const color = isCoordinator ? "info" : "success";
															const title = isCoordinator ? "Processing Now" : "Ready Now";
															const value = isCoordinator ? s.value?.processing_now : s.value?.ready_now;
															return h.div(
																{},
																h.div(
																	{ class: `bg-${color}-subtle text-${color} rounded-3 d-inline-flex p-2 mb-2` },
																	h.i({ class: `bi bi-${icon} fs-5` })
																),
																h.p({ class: "text-muted small fw-bold text-uppercase mb-1" }, title),
																h.h4({ class: "fw-bold mb-0 text-dark" }, value)
															);
														})
													)
												)
											),
											this.renderStatCard(
												"Delivering Now",
												s.value.delivering_now,
												"bicycle",
												"primary",
												"col-6"
											)
										),

										// Row 2: 4 Cards (Month Stats, Last Month, On Time, Late)
										h.div(
											{ class: "row g-3 mx-0 px-0" },
											this.renderStatCard(
												"Delivered This Month",
												s.value.delivered_this_month,
												"calendar-check",
												"secondary",
												"col-6 col-md-3"
											),
											this.renderStatCard(
												"Delivered Last Month",
												s.value.delivered_last_month,
												"calendar-range",
												"warning",
												"col-6 col-md-3"
											),
											this.renderStatCard(
												"Delivered On Time",
												s.value.delivered_on_time,
												"check-circle",
												"success",
												"col-6 col-md-3"
											),
											this.renderStatCard(
												"Delivered Late",
												s.value.delivered_late,
												"clock-history",
												"danger",
												"col-6 col-md-3"
											)
										)
									)
								)
							)
						)
					),

					// Right Column: Average Rating (Order 1 on mobile, 2 on desktop)
					h.div(
						{ class: "col-12 col-lg-4 order-1 order-lg-2" },
						v(this.ratings, (val) => {
							const ratingNum = Number(val.value) || 0;
							
							let bgGradient, titleColor, textColor, trophyColor, messageText;
							if (ratingNum < 3.0) {
								// Danger Theme
								bgGradient = "linear-gradient(135deg, #fff5f5 0%, #ffebeb 100%)";
								titleColor = "#a63a3a";
								textColor = "#c94a4a";
								trophyColor = "#dc3545";
								messageText = "Improve your performance rating!";
							} else if (ratingNum < 4.0) {
								// Warning Theme
								bgGradient = "linear-gradient(135deg, #fffaf0 0%, #fff3db 100%)";
								titleColor = "#a87d1e";
								textColor = "#c99a38";
								trophyColor = "#fd7e14";
								messageText = "Steady work! You can improve your performance rating.";
							} else {
								// Gold Success Theme
								bgGradient = "linear-gradient(135deg, #fffdf2 0%, #fff8d6 100%)";
								titleColor = "#8a6d1c";
								textColor = "#a38430";
								trophyColor = "#f7b131";
								messageText = "Top performer status in the last 90 days!";
							}

							return h.div(
								{
									class: "card border-0 shadow-sm p-4 text-center position-relative overflow-hidden h-100 d-flex flex-column align-items-center justify-content-center",
									style: `border-radius: 24px; background: ${bgGradient}; min-height: 280px;`,
								},
								// Floating stars/sparks decorations
								h.i({
									class: "bi bi-stars position-absolute opacity-25",
									style: `font-size: 3rem; left: 15px; top: 15px; color: ${trophyColor};`
								}),
								h.i({
									class: "bi bi-stars position-absolute opacity-25",
									style: `font-size: 3rem; right: 15px; bottom: 15px; color: ${trophyColor};`
								}),

								h.div(
									{ class: "d-flex flex-column align-items-center justify-content-center" },
									h.div(
										{
											class: "rounded-circle d-flex align-items-center justify-content-center mb-2 shadow-sm",
											style: `width: 56px; height: 56px; background: #fff; color: ${trophyColor}; border: 2px solid ${trophyColor}50;`,
										},
										h.i({ class: "bi bi-trophy-fill fs-4" })
									),
									h.h6(
										{ class: "fw-bold text-uppercase mb-2", style: `color: ${titleColor}; letter-spacing: 1.5px; font-size: 0.8rem;` },
										"Rider Achievement Rating"
									),
									h.h1(
										{
											class: "mb-2",
											style: `font-size: 3.5rem; font-weight: 900; color: ${titleColor}; text-shadow: 0 2px 4px ${titleColor}20;`,
										},
										ratingNum.toFixed(1)
									),
									h.div(
										{ class: "d-flex gap-1 mb-2" },
										...Array.from({ length: 5 }).map((_, idx) => {
											const starClass = idx < Math.round(ratingNum) ? "bi-star-fill" : "bi-star opacity-50";
											return h.i({ class: `bi ${starClass} fs-5`, style: `color: ${trophyColor};` });
										})
									),
									h.p(
										{ class: "mb-0 fw-semibold small", style: `color: ${textColor};` },
										messageText
									)
								)
							);
						})
					)
				)
			),
			...args
		);
	}

	renderActionCard(
		title,
		description,
		icon,
		color,
		onclick,
		colClass = "col-6 col-md-3",
	) {
		return h.div(
			{ class: colClass },
			this.renderActionCardInner(title, description, icon, color, onclick)
		);
	}

	renderActionCardInner(
		title,
		description,
		icon,
		color,
		onclick,
	) {
		const isCustomColor = color === "morning";
		const rgbVar = isCustomColor
			? "var(--ca-morning-rgb)"
			: `var(--bs-${color}-rgb)`;
		const iconColor = isCustomColor ? "#d9a406" : `var(--bs-${color})`;

		return h.div(
			{
				class: "card border-0 rounded-4 cursor-pointer hover-shadow transition-all h-100",
				style: `background-color: rgba(${rgbVar}, 0.15); color: black;`,
				onclick: onclick,
				role: "button",
			},
			h.div(
				{
					class: "card-body p-3 text-center d-flex flex-column align-items-center justify-content-center",
				},
				h.div(
					{
						class: "bg-white rounded-circle circle d-inline-flex p-3 mb-2 shadow-sm",
						style: `color: ${iconColor}; width: 52px; height: 52px; align-items: center; justify-content: center;`,
					},
					h.i({ class: `bi bi-${icon} fs-3` }),
				),
				h.h5({ class: "fw-bold mb-1 fs-6 text-dark" }, title),
				h.p(
					{
						class: "small mb-0 opacity-75 fw-500",
						style: "font-size: 0.75rem;",
					},
					description,
				),
			),
		);
	}

	renderStatCard(title, value, icon, color, colClass = "col-6 col-lg-3") {
		return h.div(
			{ class: colClass },
			h.div(
				{ class: "card border shadow-sm rounded-4 h-100" },
				h.div(
					{ class: "card-body p-3" },
					h.div(
						{
							class: `bg-${color}-subtle text-${color} rounded-3 d-inline-flex p-2 mb-2`,
						},
						h.i({ class: `bi bi-${icon} fs-5` })
					),
					h.p(
						{
							class: "text-muted small fw-bold text-uppercase mb-1",
						},
						title
					),
					h.h4({ class: "fw-bold mb-0 text-dark" }, value)
				)
			)
		);
	}

	changeOnlineStatus(status) {
		const newStatus = status === "online" ? "offline" : "online";
		broker.send(
			$e.user.needs.to.change.onlineStatus,
			payload({ status: newStatus, resetViewedUser: true })
		);
	}
}
